import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import API_CONFIG from '../config/api';
import useNetworkHierarchy from '../hooks/useNetworkHierarchy';
import DateRangePicker from '../components/DateRangePicker';
import SingleDatePicker from '../components/SingleDatePicker';
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  ChevronDownIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  DocumentArrowDownIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, formatDate } from '../utils/dateUtils';

const ContrattiPage = () => {
  const [contratti, setContratti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtri, setFiltri] = useState({
    ricerca: '',
    stato: '',
    agenzia: '',
    dataInizio: '',
    dataFine: '',
    tipologia: ''
  });
  const [paginazione, setPaginazione] = useState({
    page: 1,
    limit: 20,
    totalPages: 0,
    totalContratti: 0
  });
  const [showFiltri, setShowFiltri] = useState(false);
  const [selectedContratto, setSelectedContratto] = useState(null);
  const [showDettagliModal, setShowDettagliModal] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [contrattiFiles, setContrattiFiles] = useState([]);

  // Stati per editing inline data consegna
  const [editingContrattoId, setEditingContrattoId] = useState(null);
  const [editingDate, setEditingDate] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const [praticheCount, setPraticheCount] = useState(0);

  // Recupera utente corrente da localStorage
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user'));
  } catch (e) {
    user = null;
  }
  const ruoloUtente = (user?.role || user?.ruolo || '').toLowerCase();
  const isAdmin = ruoloUtente === 'admin';
  const isAgenzia = ruoloUtente === 'agenzia';
  const isSubagente = ruoloUtente === 'subagente' || ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale';

  // Hook per gestire la gerarchia della rete
  const { userNode, subordinateNodes, allNodes, canViewNode } = useNetworkHierarchy(user);

  // Funzione per determinare se l'utente può vedere un contratto
  const canViewContract = (contratto) => {
    // Admin vede tutto
    if (isAdmin) return true;
    
    // Se l'utente non ha un nodo associato, non può vedere nulla (tranne admin)
    if (!userNode) return false;
    
    // Controlla se il contratto appartiene all'utente stesso o ai suoi subordinati
    const contrattoAgenziaUserId = contratto.idAgenzia; // ID utente dell'agenzia
    const contrattoAttivatoreId = contratto.attivatoDa; // ID utente che ha attivato il contratto
    const contrattoAgenziaNodeId = contratto.praticaOriginale?.idAgenziaPrimaria; // ID del nodo rete
    
    // L'utente può vedere il contratto se:
    // 1. È l'agenzia del contratto (stesso utente)
    if (user.id === contrattoAgenziaUserId) return true;
    
    // 2. È l'attivatore del contratto
    if (user.id === contrattoAttivatoreId) return true;
    
    // 3. Il suo nodo è quello dell'agenzia primaria del contratto
    if (userNode.id === contrattoAgenziaNodeId) return true;
    
    // 4. Il contratto appartiene a un nodo subordinato
    const isSubordinateContract = subordinateNodes.some(node => {
      // Controlla sia per ID nodo che per ID utente
      return node.id === contrattoAgenziaNodeId || 
             node.utenteId === contrattoAgenziaUserId ||
             node.utenteId === contrattoAttivatoreId;
    });
    
    return isSubordinateContract;
  };

  // Carica numero di pratiche
  const fetchPraticheCount = async () => {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}/api/pratica`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setPraticheCount(response.data.pratiche?.length || 0);
      }
    } catch (error) {
      console.error('Errore nel caricamento del numero di pratiche:', error);
    }
  };

  // Carica contratti
  const fetchContratti = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: paginazione.page,
        limit: paginazione.limit,
        ...Object.fromEntries(Object.entries(filtri).filter(([_, value]) => value && value.trim() !== ''))
      });

      console.log('📋 Caricamento contratti con parametri:', Object.fromEntries(params));

      const response = await api.get(
        `${API_CONFIG.BASE_URL}/api/contratti?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        let contrattiData = response.data.contratti || [];
        // Aspetta che userNode sia disponibile prima di applicare i filtri
        if (!isAdmin && !userNode) {
          setContratti(contrattiData);
          setPaginazione(prev => ({
            ...prev,
            totalPages: response.data.totalPages || 0,
            totalContratti: contrattiData.length
          }));
          return;
        }
        
        // Applica filtri di visibilità basati sulla gerarchia dei nodi
        if (!isAdmin) {
          contrattiData = contrattiData.filter(contratto => canViewContract(contratto));
        }
        setContratti(contrattiData);
        setPaginazione(prev => ({
          ...prev,
          totalPages: response.data.totalPages || 0,
          totalContratti: contrattiData.length // Usa il numero filtrato
        }));
      } else {
        console.error('❌ Errore API contratti:', response.data.error);
        toast.error(response.data.error || 'Errore nel caricamento contratti');
        setContratti([]);
      }
    } catch (error) {
      console.error('Errore caricamento contratti:', error);
      toast.error('Errore nel caricamento dei contratti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContratti();
  }, [paginazione.page, JSON.stringify(filtri)]);

  // Ricarica contratti quando userNode diventa disponibile
  useEffect(() => {
    if (userNode && !isAdmin && contratti.length > 0) {
      fetchContratti();
    }
  }, [userNode]);

  // Carica il numero di pratiche quando non ci sono contratti
  useEffect(() => {
    if (contratti.length === 0 && !loading) {
      fetchPraticheCount();
    }
  }, [contratti.length, loading]);

  // Elimina un contratto attivo (solo admin) — es. contratto annullato dal cliente o dalla società
  const handleEliminaContratto = async (contratto) => {
    const cod = contratto.codiceProgressivo || `#${contratto.id}`;
    const cliente = contratto.cliente?.ragione_sociale ||
      `${contratto.cliente?.nome || ''} ${contratto.cliente?.cognome || ''}`.trim() || '';
    if (!window.confirm(`Eliminare il contratto ${cod}${cliente ? ` (${cliente})` : ''}?\n\nIl contratto sparirà dai contratti attivi. L'operazione non è reversibile.`)) return;
    try {
      const response = await api.delete(`/contratti/${contratto.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data?.success !== false) {
        toast.success(`Contratto ${cod} eliminato`);
        setContratti(prev => prev.filter(c => c.id !== contratto.id));
      } else {
        toast.error(response.data?.error || 'Errore durante l\'eliminazione');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Errore durante l\'eliminazione del contratto');
    }
  };

  // Gestione tasto Escape per annullare editing
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && editingContrattoId) {
        cancelEditingDate();
      }
    };

    if (editingContrattoId) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [editingContrattoId]);

  // Gestione filtri
  const handleFiltroChange = (campo, valore) => {
    setFiltri(prev => ({
      ...prev,
      [campo]: valore
    }));
    setPaginazione(prev => ({ ...prev, page: 1 }));
  };

  const resetFiltri = () => {
    setFiltri({
      ricerca: '',
      stato: '',
      agenzia: '',
      dataInizio: '',
      dataFine: '',
      tipologia: ''
    });
    setPaginazione(prev => ({ ...prev, page: 1 }));
  };

  // Gestione paginazione
  const handlePaginaChange = (nuovaPagina) => {
    setPaginazione(prev => ({ ...prev, page: nuovaPagina }));
  };

  // Funzioni di utilità
  const getStatoContrattoInfo = (stato) => {
    const stati = {
      'attivo': {
        label: 'Attivo',
        colore: 'bg-green-100 text-green-800 border-green-200',
        icona: <CheckCircleIcon className="h-4 w-4" />
      },
      'sospeso': {
        label: 'Sospeso',
        colore: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icona: <ClockIcon className="h-4 w-4" />
      },
      'terminato': {
        label: 'Terminato',
        colore: 'bg-gray-100 text-gray-800 border-gray-200',
        icona: <XCircleIcon className="h-4 w-4" />
      },
      'rescisso': {
        label: 'Rescisso',
        colore: 'bg-red-100 text-red-800 border-red-200',
        icona: <XCircleIcon className="h-4 w-4" />
      }
    };
    return stati[stato] || {
      label: stato,
      colore: 'bg-gray-100 text-gray-800 border-gray-200',
      icona: <DocumentTextIcon className="h-4 w-4" />
    };
  };

  const getTipologiaInfo = (tipologia) => {
    const tipologie = {
      'noleggio_breve': { label: 'Noleggio Breve', colore: 'bg-blue-100 text-blue-800' },
      'noleggio_lungo': { label: 'Noleggio Lungo', colore: 'bg-purple-100 text-purple-800' },
      'leasing': { label: 'Leasing', colore: 'bg-indigo-100 text-indigo-800' },
      'acquisto': { label: 'Acquisto', colore: 'bg-green-100 text-green-800' }
    };
    return tipologie[tipologia] || { label: tipologia, colore: 'bg-gray-100 text-gray-800' };
  };

  const getNomeCompleto = (cliente) => {
    if (!cliente) return 'Cliente non disponibile';
    
    if (cliente.tipo_cliente === 'azienda') {
      return cliente.ragione_sociale || 'Azienda';
    }
    return `${cliente.nome || ''} ${cliente.cognome || ''}`.trim() || 'Cliente';
  };



  // Funzioni per gestire i PDF
  const downloadPreventivoConTemplate = async (preventivo, praticaId) => {
    try {
      setLoadingPdf(true);
      console.log(`📄 Avvio download PDF preventivo ${preventivo.id}`);
      
      toast.info('Generazione PDF preventivo in corso...', { autoClose: 2000 });
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/pratica/${praticaId}/preventivi/${preventivo.id}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Errore nel download del PDF');
      }

      const pdfBlob = await response.blob();
      const pdfUrl = window.URL.createObjectURL(pdfBlob);

      const link = document.createElement('a');
      link.href = pdfUrl;
      
      const disposition = response.headers.get('content-disposition');
      let filename = `preventivo_${preventivo.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      if (disposition) {
        const filenameMatch = disposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(pdfUrl);
      
      toast.success('PDF preventivo scaricato con successo!');
      console.log(`✅ PDF preventivo scaricato: ${filename}`);
      
    } catch (error) {
      console.error('❌ Errore download PDF preventivo:', error);
      toast.error(error.message || 'Errore nel download del PDF del preventivo');
    } finally {
      setLoadingPdf(false);
    }
  };

  const downloadContrattoFile = async (contrattoId, praticaId) => {
    try {
      setLoadingPdf(true);
      console.log(`📄 Avvio download PDF contratto per pratica ${praticaId}`);
      
      toast.info('Download PDF contratto in corso...', { autoClose: 2000 });
      
      // Prima recuperiamo la lista dei contratti per trovare il file
      const contrattiResponse = await fetch(`${API_CONFIG.BASE_URL}/api/pratica/${praticaId}/contratti`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!contrattiResponse.ok) {
        throw new Error('Errore nel recupero lista contratti');
      }

      const contrattiData = await contrattiResponse.json();
      const contrattoFile = contrattiData.contratti?.find(c => c.id === contrattoId);

      if (!contrattoFile) {
        throw new Error('File contratto non trovato');
      }

      // Ora scarichiamo il file del contratto
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/pratica/${praticaId}/contratti/${contrattoId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Errore nel download del contratto');
      }

      const fileBlob = await response.blob();
      const fileUrl = window.URL.createObjectURL(fileBlob);

      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = contrattoFile.nome_file || `contratto_${contrattoId}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileUrl);
      
      toast.success('PDF contratto scaricato con successo!');
      console.log(`✅ PDF contratto scaricato`);
      
    } catch (error) {
      console.error('❌ Errore download contratto:', error);
      toast.error(error.message || 'Errore nel download del contratto');
    } finally {
      setLoadingPdf(false);
    }
  };

  const fetchContrattiFiles = async (praticaId) => {
    try {
      console.log(`📄 Caricamento file contratti per pratica ${praticaId}`);
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/pratica/${praticaId}/contratti`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setContrattiFiles(data.contratti || []);
        console.log(`✅ File contratti caricati: ${data.contratti?.length || 0}`);
      } else {
        console.warn('⚠️ Impossibile caricare file contratti');
        setContrattiFiles([]);
      }
    } catch (error) {
      console.error('❌ Errore caricamento file contratti:', error);
      setContrattiFiles([]);
    }
  };

  // Reset stati modal
  const resetModalStates = () => {
    setLoadingPdf(false);
    setContrattiFiles([]);
  };

  // Funzioni per editing inline data consegna
  const startEditingDate = (contratto) => {
    setEditingContrattoId(contratto.id);
    setEditingDate(contratto.dataPrevistConsegna ? 
      new Date(contratto.dataPrevistConsegna).toISOString().split('T')[0] : 
      ''
    );
  };

  const cancelEditingDate = () => {
    setEditingContrattoId(null);
    setEditingDate('');
  };

  const saveEditingDate = async () => {
    if (!editingContrattoId || !editingDate) return;

    setSavingDate(true);
    try {
      const response = await fetch(`/api/contratti/${editingContrattoId}/data-consegna`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          dataPrevistConsegna: editingDate
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Data prevista consegna aggiornata con successo!');
        
        // Aggiorna la lista locale
        setContratti(prevContratti => 
          prevContratti.map(contratto => 
            contratto.id === editingContrattoId 
              ? { ...contratto, dataPrevistConsegna: editingDate }
              : contratto
          )
        );
        
        setEditingContrattoId(null);
        setEditingDate('');
      } else {
        // Gestione specifica per contratti dalle distribuzioni salvate
        if (data.error && data.error.includes('non supporta la modifica')) {
          // Se l'utente è admin o agenzia, permette la modifica locale
          if (isAdmin || userNode?.tipo === 'agenzia_primaria') {
            toast.success('Data prevista consegna aggiornata localmente (modifica temporanea)');
            
            // Aggiorna la lista locale
            setContratti(prevContratti => 
              prevContratti.map(contratto => 
                contratto.id === editingContrattoId 
                  ? { ...contratto, dataPrevistConsegna: editingDate }
                  : contratto
              )
            );
            
            setEditingContrattoId(null);
            setEditingDate('');
          } else {
            toast.error('Questo contratto non supporta la modifica della data di consegna. Contatta l\'amministratore per assistenza.');
          }
        } else {
          toast.error(data.error || 'Errore nell\'aggiornamento della data');
        }
      }
    } catch (error) {
      console.error('Errore durante l\'aggiornamento:', error);
      toast.error('Errore durante l\'aggiornamento della data');
    } finally {
      setSavingDate(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestione Contratti</h1>
              <p className="mt-1 text-sm text-gray-500">
                {paginazione.totalContratti} contratti {isAdmin ? 'attivi nel sistema' : 'visibili per te'}
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFiltri(!showFiltri)}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium transition-colors ${
                  showFiltri 
                    ? 'bg-blue-50 text-blue-700 border-blue-300' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filtri
                <ChevronDownIcon className={`h-4 w-4 ml-2 transition-transform ${showFiltri ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Filtri */}
        {showFiltri && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtri di Ricerca</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ricerca Cliente/Codice
                  </label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      value={filtri.ricerca}
                      onChange={(e) => handleFiltroChange('ricerca', e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Cerca per cliente o codice contratto..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stato Contratto
                  </label>
                  <select
                    value={filtri.stato}
                    onChange={(e) => handleFiltroChange('stato', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tutti gli stati</option>
                    <option value="attivo">Attivo</option>
                    <option value="sospeso">Sospeso</option>
                    <option value="terminato">Terminato</option>
                    <option value="rescisso">Rescisso</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipologia
                  </label>
                  <select
                    value={filtri.tipologia}
                    onChange={(e) => handleFiltroChange('tipologia', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tutte le tipologie</option>
                    <option value="noleggio_breve">Noleggio Breve</option>
                    <option value="noleggio_lungo">Noleggio Lungo</option>
                    <option value="leasing">Leasing</option>
                    <option value="acquisto">Acquisto</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Periodo Attivazione
                  </label>
                  <DateRangePicker
                    startDate={filtri.dataInizio}
                    endDate={filtri.dataFine}
                    onDatesChange={({ startDate, endDate }) => {
                      setFiltri(prev => ({
                        ...prev,
                        dataInizio: startDate || '',
                        dataFine: endDate || ''
                      }));
                      setPaginazione(prev => ({ ...prev, page: 1 }));
                    }}
                    placeholderStart="Data inizio"
                    placeholderEnd="Data fine"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={resetFiltri}
                    className="w-full px-4 py-2.5 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Reset Filtri
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista Contratti */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-500">Caricamento contratti...</p>
            </div>
          ) : contratti.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Codice Progressivo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Agenzia
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data Attivazione
                      </th>
                      
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipologia
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data Prevista Consegna
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stato
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {contratti.map((contratto) => {
                      if (!contratto || !contratto.cliente) {
                        console.warn('⚠️ Contratto o cliente mancante:', contratto);
                        return null;
                      }
                      
                      const statoInfo = getStatoContrattoInfo(contratto.statoContratto || 'sconosciuto');
                      const tipologiaInfo = getTipologiaInfo(contratto.tipologia || 'non_specificata');
  
                      const cliente = getNomeCompleto(contratto.cliente);
                      
                      return (
                        <tr key={contratto.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">
                                {contratto.codiceProgressivo || 'N/A'}
                              </span>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {cliente === 'N/D' || cliente === 'Cliente non disponibile' ? (
                                    <span className="text-red-600">Cliente non disponibile</span>
                                  ) : (
                                    cliente
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {contratto.cliente.email && contratto.cliente.email !== '' ? (
                                    contratto.cliente.email
                                  ) : (
                                    <span className="text-orange-500">Email non disponibile</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                              <div>
                                {contratto.agenziaInfo ? (
                                  <>
                                    <div className="text-sm font-medium text-gray-900">
                                      {contratto.agenziaInfo.nomeAgenzia}
                                    </div>
                                    {contratto.agenteInfo ? (
                                      <div className="text-sm text-gray-500">
                                        Agente: {contratto.agenteInfo.nome} {contratto.agenteInfo.cognome}
                                      </div>
                                    ) : contratto.agenziaInfo.referente && (
                                      <div className="text-sm text-gray-500">
                                        Ref: {contratto.agenziaInfo.referente.nome} {contratto.agenziaInfo.referente.cognome}
                                      </div>
                                    )}
                                  </>
                                ) : contratto.agenteInfo ? (
                                  <div className="text-sm text-gray-900">
                                    Agente: {contratto.agenteInfo.nome} {contratto.agenteInfo.cognome}
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-900">
                                    {contratto.agenzia?.nome} {contratto.agenzia?.cognome}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <CalendarDaysIcon className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">
                                {formatDate(contratto.dataAttivazione)}
                              </span>
                            </div>
                          </td>
                          

                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tipologiaInfo.colore}`}>
                              {tipologiaInfo.label}
                            </span>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            {editingContrattoId === contratto.id ? (
                              <div className="flex items-center space-x-2">
                                <SingleDatePicker
                                  selectedDate={editingDate}
                                  onChange={(date) => setEditingDate(date)}
                                  placeholder="Seleziona data"
                                  disabled={savingDate}
                                  autoFocus={true}
                                  className="min-w-[180px]"
                                />
                                <button
                                  onClick={saveEditingDate}
                                  disabled={savingDate || !editingDate}
                                  className="p-1 text-green-600 hover:text-green-800 disabled:text-gray-400"
                                  title="Salva"
                                >
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={cancelEditingDate}
                                  disabled={savingDate}
                                  className="p-1 text-red-600 hover:text-red-800 disabled:text-gray-400"
                                  title="Annulla"
                                >
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ) : contratto.dataPrevistConsegna ? (
                              <button
                                onClick={() => startEditingDate(contratto)}
                                className="flex items-center hover:bg-gray-50 p-1 rounded"
                                title="Clicca per modificare"
                              >
                                <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-900">
                                  {formatDate(contratto.dataPrevistConsegna)}
                                </span>
                              </button>
                            ) : (
                              <button
                                onClick={() => startEditingDate(contratto)}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                Non specificata - Clicca per inserire
                              </button>
                            )}
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statoInfo.colore}`}>
                              {statoInfo.icona}
                              <span className="ml-1">{statoInfo.label}</span>
                            </span>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  if (contratto.praticaOriginale?.id) {
                                    window.open(`/nlt/pratiche/${contratto.praticaOriginale.id}`, '_blank');
                                  } else {
                                    toast.error('Pratica non disponibile per questo contratto');
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-900 transition-colors"
                                title="Visualizza pratica"
                              >
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  setSelectedContratto(contratto);
                                  setShowDettagliModal(true);
                                  // Carica i file contratti se c'è una pratica associata
                                  if (contratto.praticaOriginale?.id) {
                                    await fetchContrattiFiles(contratto.praticaOriginale.id);
                                  }
                                }}
                                className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                title="Dettagli contratto"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={() => handleEliminaContratto(contratto)}
                                  className="text-red-600 hover:text-red-900 transition-colors"
                                  title="Elimina contratto"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Paginazione */}
              {paginazione.totalPages > 1 && (
                <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handlePaginaChange(paginazione.page - 1)}
                        disabled={paginazione.page === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Precedente
                      </button>
                      <button
                        onClick={() => handlePaginaChange(paginazione.page + 1)}
                        disabled={paginazione.page === paginazione.totalPages}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Successivo
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Visualizzazione <span className="font-medium">{((paginazione.page - 1) * paginazione.limit) + 1}</span> -{' '}
                          <span className="font-medium">
                            {Math.min(paginazione.page * paginazione.limit, paginazione.totalContratti)}
                          </span>{' '}
                          di <span className="font-medium">{paginazione.totalContratti}</span> contratti
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => handlePaginaChange(paginazione.page - 1)}
                            disabled={paginazione.page === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Precedente
                          </button>
                          {Array.from({ length: paginazione.totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => handlePaginaChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === paginazione.page
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            onClick={() => handlePaginaChange(paginazione.page + 1)}
                            disabled={paginazione.page === paginazione.totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Successivo
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-8 text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun contratto trovato</h3>
              <p className="text-gray-500 mb-6">
                {Object.values(filtri).some(v => v) 
                  ? 'Prova a modificare i filtri di ricerca' 
                  : 'Non ci sono contratti attivi nel sistema. I contratti vengono creati dalle pratiche quando vengono attivati.'
                }
              </p>
              
              {/* Azioni suggerite */}
              <div className="space-y-3">
                <div className="flex justify-center space-x-4">
                  <a
                    href="/nlt/pratiche"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                    Visualizza Pratiche
                  </a>
                  
                  {isAdmin || isAgenzia ? (
                    <a
                      href="/nlt/pratiche"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <DocumentTextIcon className="h-4 w-4 mr-2" />
                      Crea Contratto
                    </a>
                  ) : null}
                </div>
                
                <p className="text-xs text-gray-400 mt-4">
                  💡 I contratti vengono generati automaticamente quando una pratica viene attivata e approvata
                  {praticheCount > 0 && (
                    <span className="block mt-1">
                      📋 Hai {praticheCount} pratica{praticheCount !== 1 ? 'he' : ''} disponibili
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Dettagli Contratto */}
      {showDettagliModal && selectedContratto && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            
            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              {/* Header */}
              <div className="bg-white px-4 pt-4 pb-4 sm:p-6 sm:pb-4 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Dettagli Contratto - {selectedContratto.codiceProgressivo || 'N/A'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowDettagliModal(false);
                      setSelectedContratto(null);
                      setContrattiFiles([]);
                      setLoadingPdf(false);
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              {/* Body */}
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="sm:flex sm:items-start">
                  {/* Colonna sinistra: Riepilogo contratto */}
                  <div className="sm:w-1/3 mb-4 sm:mb-0 sm:mr-4">
                    {/* Stato e Tipologia */}
                    <div className="border border-gray-200 rounded-lg p-4 mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Stato Contratto</h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500">Stato Corrente</p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatoContrattoInfo(selectedContratto.statoContratto).colore}`}>
                            {getStatoContrattoInfo(selectedContratto.statoContratto).icona}
                            <span className="ml-1">{getStatoContrattoInfo(selectedContratto.statoContratto).label}</span>
                          </span>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Tipologia</p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTipologiaInfo(selectedContratto.tipologia).colore}`}>
                            {getTipologiaInfo(selectedContratto.tipologia).label}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Canone Mensile</p>
                          <p className="text-lg font-bold text-blue-600">
                            {selectedContratto.canoneMessile ? formatCurrency(selectedContratto.canoneMessile) : 'N/A'}
                            <span className="text-sm font-normal ml-1">IVA escl.</span>
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Cliente */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Cliente</h4>
                      <div className="space-y-2">
                        <div>
                          <p className="font-medium">{getNomeCompleto(selectedContratto.cliente)}</p>
                        </div>
                        {selectedContratto.cliente.email && (
                          <div>
                            <p className="text-sm text-gray-500">Email</p>
                            <p className="text-sm">{selectedContratto.cliente.email}</p>
                          </div>
                        )}
                        {selectedContratto.cliente.telefono && (
                          <div>
                            <p className="text-sm text-gray-500">Telefono</p>
                            <p className="text-sm">{selectedContratto.cliente.telefono}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Colonna destra: Dettagli contratto */}
                  <div className="sm:w-2/3">
                    {/* Informazioni generali */}
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Informazioni Contratto</h4>
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500">Codice Progressivo</p>
                            <p className="font-medium">{selectedContratto.codiceProgressivo || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Data Attivazione</p>
                            <p className="font-medium">{formatDate(selectedContratto.dataAttivazione)}</p>
                          </div>

                          {selectedContratto.dataPrevistConsegna && (
                            <div>
                              <p className="text-sm text-gray-500">Data Prevista Consegna</p>
                              <p className="font-medium">{formatDate(selectedContratto.dataPrevistConsegna)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Agenzia */}
                    {selectedContratto.agenziaInfo && (
                      <div className="mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Agenzia</h4>
                        <div className="border border-gray-200 rounded-lg p-4">
                          <div>
                            <p className="text-sm text-gray-500">Nome Agenzia</p>
                            <p className="font-medium">{selectedContratto.agenziaInfo.nomeAgenzia}</p>
                          </div>
                          {selectedContratto.agenziaInfo.referente && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-500">Referente</p>
                              <p className="font-medium">
                                {selectedContratto.agenziaInfo.referente.nome} {selectedContratto.agenziaInfo.referente.cognome}
                              </p>
                              {selectedContratto.agenziaInfo.referente.email && (
                                <p className="text-sm text-gray-600">{selectedContratto.agenziaInfo.referente.email}</p>
                              )}
                            </div>
                          )}
                          {selectedContratto.agenteInfo && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-500">Agente che ha gestito</p>
                              <p className="font-medium">
                                {selectedContratto.agenteInfo.nome} {selectedContratto.agenteInfo.cognome}
                              </p>
                              {selectedContratto.agenteInfo.email && (
                                <p className="text-sm text-gray-600">{selectedContratto.agenteInfo.email}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Documenti */}
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Documenti</h4>
                      <div className="space-y-3">
                        {/* PDF Preventivo */}
                        {selectedContratto.preventivo && (
                          <div className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <ClipboardDocumentListIcon className="h-5 w-5 text-blue-500 mr-2" />
                                <div>
                                  <p className="font-medium text-gray-900">Preventivo PDF</p>
                                  <p className="text-xs text-gray-500">ID: {selectedContratto.preventivo.id} | Tipo: {selectedContratto.preventivo.tipoPreventivo}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  if (selectedContratto.praticaOriginale?.id && selectedContratto.preventivo?.id) {
                                    downloadPreventivoConTemplate(selectedContratto.preventivo, selectedContratto.praticaOriginale.id);
                                  } else {
                                    toast.error('Dati preventivo non disponibili');
                                  }
                                }}
                                disabled={loadingPdf || !selectedContratto.praticaOriginale?.id}
                                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Scarica PDF Preventivo"
                              >
                                <DocumentArrowDownIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* File Contratti caricati */}
                        {contrattiFiles.length > 0 ? (
                          contrattiFiles.map((file) => (
                            <div key={file.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <DocumentTextIcon className="h-5 w-5 text-green-500 mr-2" />
                                  <div>
                                    <p className="font-medium text-gray-900">{file.nome_file}</p>
                                    <p className="text-xs text-gray-500">
                                      Tipo: {file.tipo} | Caricato il {formatDate(file.createdAt)}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    if (selectedContratto.praticaOriginale?.id) {
                                      downloadContrattoFile(file.id, selectedContratto.praticaOriginale.id);
                                    } else {
                                      toast.error('Dati contratto non disponibili');
                                    }
                                  }}
                                  disabled={loadingPdf}
                                  className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Scarica File Contratto"
                                >
                                  <DocumentArrowDownIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                              <div>
                                <p className="font-medium text-gray-500">Nessun file contratto caricato</p>
                                <p className="text-xs text-gray-400">I file contratti verranno mostrati qui quando caricati nella pratica</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Note */}
                    {selectedContratto.note && (
                      <div className="mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Note</h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-900">{selectedContratto.note}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {selectedContratto.preventivo && selectedContratto.praticaOriginale?.id && (
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                      downloadPreventivoConTemplate(selectedContratto.preventivo, selectedContratto.praticaOriginale.id);
                    }}
                    disabled={loadingPdf}
                  >
                    {loadingPdf ? 'Generando...' : 'Scarica Preventivo'}
                  </button>
                )}
                {selectedContratto.praticaOriginale?.id && (
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => {
                      window.open(`/nlt/pratiche/${selectedContratto.praticaOriginale.id}`, '_blank');
                    }}
                  >
                    Visualizza Pratica
                  </button>
                )}
                                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => {
                      setShowDettagliModal(false);
                      setSelectedContratto(null);
                      setContrattiFiles([]);
                      setLoadingPdf(false);
                    }}
                  >
                    Chiudi
                  </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContrattiPage; 